import { motion } from "framer-motion";
import { Flame, Zap, HardHat, Factory } from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/animations";

const industries = [
  { icon: Flame, name: "Oil & Gas", desc: "Upstream and midstream project delivery, valve and instrumentation programs." },
  { icon: Zap, name: "Power", desc: "Generation, transmission, and grid modernization initiatives." },
  { icon: HardHat, name: "EPC", desc: "Engineering, Procurement & Construction lifecycle — quoting to commissioning." },
  { icon: Factory, name: "Industrial Engineering", desc: "Process equipment, automation, and manufacturing rollouts." },
];

export const Industries = () => {
  return (
    <section id="industries" className="relative py-24 md:py-32 overflow-hidden" data-testid="industries-section">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1765405016584-0ad9f20e2ff9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxvaWwlMjBhbmQlMjBnYXMlMjBlbmdpbmVlcmluZyUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3Nzg3MDM0NjZ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#051024] via-[#051024]/95 to-[#0A192F]" />
      </div>

      <div className="relative px-6 md:px-12 lg:px-24">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="max-w-3xl mb-16"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-4 mb-6">
            <div className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Industries Served</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-[#F8F9FA] leading-tight text-balance" data-testid="industries-heading">
            Heavy industry needs <em className="text-[#D4AF37] font-medium">heavy-discipline</em> delivery.
          </motion.h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {industries.map((ind) => (
            <motion.div
              key={ind.name}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              className="group relative bg-[#0A192F]/80 backdrop-blur-sm border border-[#233554] hover:border-[#D4AF37] p-8 md:p-10 transition-all"
              data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
            >
              <ind.icon size={36} strokeWidth={1.25} className="text-[#D4AF37] mb-6" />
              <p className="font-serif text-2xl text-[#F8F9FA] mb-3">{ind.name}</p>
              <p className="text-sm text-[#A8B2D1] leading-relaxed">{ind.desc}</p>
              <div className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full bg-[#D4AF37] transition-all duration-500" />
            </motion.div>
          ))}
        </motion.div>

        {/* Geography strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.7 }}
          className="mt-16 border-t border-[#233554] pt-10"
          data-testid="geography-strip"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-6">Global Footprint</p>
          <div className="flex flex-wrap gap-x-12 gap-y-4 font-serif text-2xl md:text-3xl text-[#F8F9FA]">
            {["Germany", "Italy", "United Kingdom", "Spain", "Singapore", "Brazil", "India", "United States", "France"].map((country) => (
              <span key={country} className="hover:text-[#D4AF37] transition-colors">{country}</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
